import { JsonRpcProvider } from '@ethersproject/providers';
import { Actions, Connector, ProviderConnectInfo, ProviderRpcError } from '@web3-react/types';

/**
 * @function
 * @description Parses the hexadecimal string representation of chainId into its numeric value.
 * @param {string} chainId - The hexadecimal string representation of chainId.
 * @returns {number} The numeric value of chainId.
 */
export function parseChainId(chainId: string): number {
  return Number.parseInt(chainId, 16);
}

/**
 * @class
 * @description A JSON-RPC connector class that extends the Connector class and provides a custom provider for the connection.
 * @extends {Connector}
 */
export default class JsonRpcConnector extends Connector {
  /**
   * The custom provider for the JSON-RPC connection.
   * @type {JsonRpcProvider}
   */
  public customProvider: JsonRpcProvider;

  /**
   * Creates an instance of JsonRpcConnector.
   * @param {{
   *     actions: Actions;
   *     provider: JsonRpcProvider;
   *     onError?: (error: Error) => void;
   * }} {
   *     - actions - The Actions object that contains the update and reset state methods.
   *     - provider - The JsonRpcProvider object for the connection.
   *     - onError - [Optional] The error handler method for the connection.
   *
   * }
   */
  constructor({
    actions,
    provider,
    onError,
  }: {
    actions: Actions;
    provider: JsonRpcProvider;
    onError?: (error: Error) => void;
  }) {
    super(actions, onError);

    /**
     * The custom provider for the JSON-RPC connection.
     * @type {JsonRpcProvider}
     */
    this.customProvider = provider
      .on('connect', ({ chainId }: ProviderConnectInfo): void => {
        this.actions.update({ chainId: parseChainId(chainId) });
      })
      .on('disconnect', (error: ProviderRpcError): void => {
        this.onError?.(error);
        this.actions.resetState();
      })
      .on('chainChanged', (chainId: string): void => {
        this.actions.update({ chainId: parseChainId(chainId) });
      })
      .on('accountsChanged', (accounts: string[]): void => {
        this.actions.update({ accounts });
      });
  }

  /**
   * @async
   * @function
   * @description Activates the JSON-RPC connection and retrieves the chain ID and accounts information.
   * @returns {Promise<void>} Resolves when the activation is successful and updates the state with the retrieved information.
   * @throws {Error} If the activation is unsuccessful.
   */
  async activate(): Promise<void> {
    this.actions.startActivation();

    try {
      const [{ chainId }, accounts] = await Promise.all([
        this.customProvider.getNetwork(),
        this.customProvider.listAccounts(),
      ]);
      this.actions.update({ chainId, accounts });
    } catch (e) {
      this.actions.resetState();
      throw e;
    }
  }
}
